import { useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import { Link } from "react-router-dom";
import { NavLink } from "./NavLink";
import LanguageToggle from "./LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalePath } from "@/hooks/useLocalePath";
import logoImage from "@/assets/logo.png";
import { Menu, X } from "lucide-react";

const Header = () => {
  const { language } = useLanguage();
  const { getPath } = useLocalePath();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to={getPath("/")} className="flex items-center gap-2 group whitespace-nowrap shrink-0">
          <img
            src={logoImage}
            alt="The Wise Compass"
            className="h-10 w-10 object-contain"
            width={40}
            height={40}
            loading="eager"
            // @ts-ignore
            fetchpriority="high"
          />
          <span className="font-serif text-xl text-foreground">
            The Wise <span className="text-primary">Compass</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink
            to={getPath("/")}
            end
            className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            activeClassName="text-primary font-semibold"
          >
            {language === "fr" ? "Accueil" : "Home"}
          </NavLink>
          <NavLink
            to={getPath("/explorer")}
            className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            activeClassName="text-primary font-semibold"
          >
            {language === "fr" ? "Explorer les marques" : "Explore brands"}
          </NavLink>
          <NavLink
            to={getPath("/compare")}
            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-bold text-sm px-4 py-1.5 rounded-full hover:bg-primary/90 transition-colors"
            activeClassName="ring-2 ring-primary/30"
          >
            <ArrowLeftRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            {language === "fr" ? "Comparateur" : "Compare"}
          </NavLink>
          <NavLink
            to={getPath("/top-10")}
            className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            activeClassName="text-primary font-semibold"
          >
            {language === "fr" ? "Nos sélections" : "Our Selections"}
          </NavLink>
          <NavLink
            to={getPath("/blog")}
            className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            activeClassName="text-primary font-semibold"
          >
            Blog
          </NavLink>
        </nav>

        {/* Desktop language toggle */}
        <div className="hidden md:flex items-center">
          <LanguageToggle />
        </div>

        {/* Mobile: language toggle + burger */}
        <div className="flex md:hidden items-center gap-3">
          <LanguageToggle />
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
            <NavLink
              to={getPath("/")}
              end
              className="text-muted-foreground hover:text-foreground transition-colors text-base font-medium py-2"
              activeClassName="text-primary font-semibold"
              onClick={() => setMenuOpen(false)}
            >
              {language === "fr" ? "Accueil" : "Home"}
            </NavLink>
            <NavLink
              to={getPath("/explorer")}
              className="text-muted-foreground hover:text-foreground transition-colors text-base font-medium py-2"
              activeClassName="text-primary font-semibold"
              onClick={() => setMenuOpen(false)}
            >
              {language === "fr" ? "Explorer les marques" : "Explore brands"}
            </NavLink>
            <NavLink
              to={getPath("/compare")}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-bold text-base px-4 py-2 rounded-full hover:bg-primary/90 transition-colors w-fit"
              activeClassName="ring-2 ring-primary/30"
              onClick={() => setMenuOpen(false)}
            >
              <ArrowLeftRight className="w-4 h-4" strokeWidth={2.5} />
              {language === "fr" ? "Comparateur" : "Compare"}
            </NavLink>
            <NavLink
              to={getPath("/top-10")}
              className="text-muted-foreground hover:text-foreground transition-colors text-base font-medium py-2"
              activeClassName="text-primary font-semibold"
              onClick={() => setMenuOpen(false)}
            >
              {language === "fr" ? "Nos sélections" : "Our Selections"}
            </NavLink>
            <NavLink
              to={getPath("/blog")}
              className="text-muted-foreground hover:text-foreground transition-colors text-base font-medium py-2"
              activeClassName="text-primary font-semibold"
              onClick={() => setMenuOpen(false)}
            >
              Blog
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
